import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Notice Background Component
 *
 * Az értesítő/befejező nézetek teljes képernyős háttere:
 * - ha van admin által feltöltött háttér (imageUrl): kép VAGY videó
 *   (type szerint) + sötét overlay; videó-hibánál gradient+canvas fallback,
 * - különben lila gradient + canvas-on szinuszosan hullámzó pontmező.
 *
 * A vizuális konstansok a www/js/module/noticebg.js vanilla implementációval
 * szinkronban tartandók (a két buildrendszer miatt duplikált a rajzoló kód).
 */
import React, { useEffect, useRef, useState } from 'react';
import './NoticeBg.scss';
export function NoticeBg({ imageUrl, type }) {
    const canvasRef = useRef(null);
    // Közös média-hiba state (kép ÉS videó): hibánál gradient+canvas fallback.
    // A kép valódi <img>-ként renderelődik (onError esemény CSS backgroundnál
    // nincs); prop-váltásra a state visszaáll, hogy az új média esélyt kapjon.
    const [mediaFailed, setMediaFailed] = useState(false);
    useEffect(() => {
        setMediaFailed(false);
    }, [imageUrl, type]);
    const isVideo = type === 'video';
    const hasMedia = !!imageUrl && !mediaFailed;
    useEffect(() => {
        if (hasMedia) {
            return;
        }
        const canvas = canvasRef.current;
        if (!canvas) {
            return;
        }
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            return;
        }
        // DPR sapka: 2 felett a rajzolási költség nő, látható haszon nélkül
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        let width = 0;
        let height = 0;
        let cols = 110;
        let rafId = 0;
        let stopped = false;
        const resize = () => {
            width = canvas.clientWidth || window.innerWidth;
            height = canvas.clientHeight || window.innerHeight;
            canvas.width = Math.floor(width * dpr);
            canvas.height = Math.floor(height * dpr);
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
            // Mobilon ritkább pontrács
            cols = Math.max(50, Math.min(110, Math.round(width / 14)));
        };
        const ROWS = 30;
        const draw = (time) => {
            ctx.clearRect(0, 0, width, height);
            for (let r = 0; r < ROWS; r++) {
                const depth = r / (ROWS - 1); // 0 = távoli sor, 1 = közeli sor
                const baseY = height * (0.58 + depth * 0.4);
                const amplitude = 12 + depth * 30;
                const radius = 0.6 + depth * 1.9;
                const alpha = 0.1 + depth * 0.45;
                ctx.fillStyle = `rgba(196,153,255,${alpha.toFixed(3)})`;
                for (let c = 0; c <= cols; c++) {
                    const x = (c / cols) * width;
                    const wave = Math.sin(c * 0.22 + r * 0.35 + time * 0.0011) +
                        Math.sin(c * 0.07 - time * 0.0007 + r * 0.18) * 0.6;
                    const y = baseY + wave * amplitude;
                    ctx.beginPath();
                    ctx.arc(x, y, radius, 0, 6.2832);
                    ctx.fill();
                }
            }
        };
        resize();
        window.addEventListener('resize', resize);
        let reducedMotion = false;
        try {
            reducedMotion =
                !!window.matchMedia &&
                    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        }
        catch (e) {
            // matchMedia nem elérhető — animálunk
        }
        if (reducedMotion) {
            draw(0);
        }
        else {
            const loop = (timestamp) => {
                if (stopped) {
                    return;
                }
                draw(timestamp);
                rafId = window.requestAnimationFrame(loop);
            };
            rafId = window.requestAnimationFrame(loop);
        }
        return () => {
            stopped = true;
            if (rafId) {
                window.cancelAnimationFrame(rafId);
            }
            window.removeEventListener('resize', resize);
        };
    }, [hasMedia]);
    if (hasMedia) {
        return (_jsxs("div", { className: "noticebg", children: [_jsx("div", { className: "noticebg-gradient" }), isVideo ? (_jsx("video", { className: "noticebg-video", src: imageUrl, autoPlay: true, muted: true, loop: true, playsInline: true, onError: () => setMediaFailed(true) })) : (_jsx("img", { className: "noticebg-image", src: imageUrl, alt: "", "aria-hidden": "true", onError: () => setMediaFailed(true) })), _jsx("div", { className: "noticebg-overlay" })] }));
    }
    return (_jsxs("div", { className: "noticebg", children: [_jsx("div", { className: "noticebg-gradient" }), _jsx("canvas", { ref: canvasRef, className: "noticebg-canvas", "aria-hidden": "true" })] }));
}
